import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import TableActionButton from "@components/buttons/TableActionButton";
import CopyButton from "@components/buttons/CopyButton";
import { affiliateApis } from "@services/apis/Affiliate";
import { ApproveMerchantRequest } from "@services/models/Affiliate/ApproveMerchantModel";
import {
  MerchantApprovalsData,
  MerchantApprovalsResponse,
} from "@services/models/Affiliate/MerchantApprovalMappingModel";
import { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ConstantFunctions from "../../utils/constants_functions";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import AffiliateStatusComponent from "./components/AffiliateStatusPill";

const MerchantApprovalMapping = () => {
  const constantFunctions = new ConstantFunctions();
  const gridRef = useRef<any>(null);

  const [approvals, setApprovals] = useState<MerchantApprovalsData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const rowsPerPage = 1000;

  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const { handleError, handleSuccess } = useSnackbarMessage();

  // AG-Grid Column Definitions
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Date",
        field: "createdAt",
        flex: 1,
        minWidth: 130,
        maxWidth: 180,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
        cellRenderer: (params: any) => {
          return constantFunctions.dateFormatInDDMMYY(params.data.createdAt);
        },
      },
      {
        headerName: "Affiliate User ID",
        field: "affiliateUserId",
        flex: 1,
        minWidth: 120,
        maxWidth: 170,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center justify-between">
              <span className="flex-1">{params.data.affiliateUserId}</span>
              <CopyButton
                copyText={params.data.affiliateUserId.toString()}
                copyMessage="Affiliate User ID copied!"
                buttonSize="14px"
                copyIconSize={10}
                borderRadius="2px"
              />
            </div>
          );
        },
      },
      {
        headerName: "Affiliate Email ID",
        field: "affiliateEmail",
        flex: 2.2,
        minWidth: 250,
        maxWidth: 380,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center justify-between">
              <span className="flex-1 truncate pr-1">
                {params.data.affiliateEmail}
              </span>
              <CopyButton
                copyText={params.data.affiliateEmail}
                copyMessage="Affiliate Email copied!"
                buttonSize="14px"
                copyIconSize={10}
                borderRadius="2px"
              />
            </div>
          );
        },
      },
      {
        headerName: "Merchant Email ID",
        field: "merchantEmail",
        flex: 2.2,
        minWidth: 250,
        maxWidth: 380,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center justify-between">
              <span className="flex-1 truncate pr-1">
                {params.data.merchantEmail}
              </span>
              <CopyButton
                copyText={params.data.merchantEmail}
                copyMessage="Merchant Email copied!"
                buttonSize="14px"
                copyIconSize={10}
                borderRadius="2px"
              />
            </div>
          );
        },
      },
      {
        headerName: "Status",
        field: "status",
        flex: 1,
        minWidth: 120,
        maxWidth: 170,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <AffiliateStatusComponent
              statusText={params.data.status.toUpperCase()}
            />
          );
        },
      },
      {
        headerName: "Map Merchant",
        field: "actions",
        flex: 1.5,
        minWidth: 220,
        maxWidth: 280,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          if (params.data.status === "requested") {
            return (
              <div className="flex flex-row items-center gap-1.5 justify-end">
                <TableActionButton
                  variant="danger"
                  icon="cancel"
                  loading={isButtonLoading}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleApprovalStatus(params.data.id, "rejected");
                  }}
                >
                  Reject
                </TableActionButton>
                <TableActionButton
                  variant="success"
                  icon="check_circle"
                  loading={isButtonLoading}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleApprovalStatus(params.data.id, "approved");
                  }}
                >
                  Approve
                </TableActionButton>
              </div>
            );
          }
          return null;
        },
      },
    ],
    [isButtonLoading],
  );

  const onBodyScroll = (params: any) => {
    const scrollTop = params.top;
    const scrollHeight = params.api.getDisplayedRowCount() * 48;
    const clientHeight =
      params.api.getVerticalPixelRange().bottom -
      params.api.getVerticalPixelRange().top;

    if (scrollTop + clientHeight >= scrollHeight - 100 && hasMore && !loading) {
      loadMoreApprovals();
    }
  };

  const fetchApprovalList = useCallback(
    async (pageNumber: number, append: boolean) => {
      try {
        setLoading(true);
        const response: MerchantApprovalsResponse =
          await affiliateApis.getMerchantApprovals({
            page: pageNumber,
            limit: rowsPerPage,
          });

        const fetched = response.data;
        setApprovals((prev) => (append ? [...prev, ...fetched] : fetched));
        setHasMore(fetched.length === rowsPerPage);
      } catch (err: any) {
        setError("Failed to fetch approval data");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const loadMoreApprovals = useCallback(() => {
    if (loading || !hasMore) return;
    setPage((prev) => {
      const next = prev + 1;
      fetchApprovalList(next, true);
      return next;
    });
  }, [loading, hasMore, fetchApprovalList]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchApprovalList(1, false);
  }, [fetchApprovalList]);

  const handleApprovalStatus = async (
    id: number,
    status: "approved" | "rejected",
  ) => {
    try {
      setIsButtonLoading(true);
      const updateRequest: ApproveMerchantRequest = {
        id: id,
        status: status,
      };

      const data = await affiliateApis.approveMerchant(updateRequest);

      setApprovals((prevApprovals) =>
        prevApprovals.map((approval) => {
          if (approval.id === id) {
            return {
              ...approval,
              status: status,
            };
          }
          return approval;
        }),
      );

      handleSuccess({ message: data.msg || "Status updated successfully" });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.msg ||
        "Updating status failed. Please try again.";
      handleError({ message: errorMessage });
    } finally {
      setIsButtonLoading(false);
    }
  };

  return (
    <>
      <div
        className="flex flex-col bg-bg-page-body py-[20px] overflow-hidden gap-4"
        style={{ height: "calc(100dvh - 120px)" }}
      >
        <div className="overflow-hidden" style={{ height: "100%" }}>
          {loading && approvals.length === 0 ? (
            <AgGridSkeleton
              columnDefs={columnDefs}
              style={{ height: "100%", width: "100%" }}
            />
          ) : (
            <AgGridReactTable
              columnDefs={columnDefs}
              rowData={approvals}
              theme="legacy"
              onBodyScroll={onBodyScroll}
              ref={gridRef}
              error={error}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default MerchantApprovalMapping;
